import React from "react";
import { Link, NavLink } from "react-router-dom";
import HnbLogo from "../../components/brand/HnbLogo";
import { adminTheme } from "../../components/admin/adminTheme";
import { useAuth } from "../../hooks/useAuth";
import { getStaffDashboardPath } from "../../auth/roleRoutes";

type TopNavProps = {
  onMenuToggle: () => void;
};

const topLinkStyle: React.CSSProperties = {
  textDecoration: "none",
  color: adminTheme.color.textSoft,
  fontWeight: 600,
  fontSize: "0.95rem",
  padding: "0.55rem 0.8rem",
  borderRadius: 999,
};

const menuButtonStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 12,
  border: `1px solid ${adminTheme.color.borderStrong}`,
  background: adminTheme.color.surface,
  color: adminTheme.color.text,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
};

const logoutButtonStyle: React.CSSProperties = {
  border: `1px solid ${adminTheme.color.border}`,
  background: adminTheme.color.surface,
  color: adminTheme.color.text,
  padding: "0.7rem 1rem",
  borderRadius: 999,
  fontWeight: 700,
  fontSize: "0.95rem",
  cursor: "pointer",
  transition: adminTheme.transition.base,
};

const getSignedInLabel = (name?: string, email?: string): string => {
  if (name && name.trim().length > 0) {
    return `Signed in as ${name}`;
  }

  if (email && email.trim().length > 0) {
    return `Signed in as ${email}`;
  }

  return "Admin dashboard";
};

export default function TopNav({ onMenuToggle }: TopNavProps) {
  const { user, logout } = useAuth();

  const dashboardHomePath = getStaffDashboardPath(user?.staffSubRole ?? null);

  return (
    <header className="app-shell-topnav">
      <div className="app-shell-topnav__left">
        <button
          type="button"
          onClick={onMenuToggle}
          aria-label="Open dashboard navigation"
          className="app-shell-menu-button"
          style={menuButtonStyle}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 7H20M4 12H20M4 17H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <Link
          to={dashboardHomePath}
          style={{
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            minWidth: 0,
          }}
          aria-label="Go to dashboard home"
        >
          <HnbLogo />
        </Link>

        <span className="app-shell-topnav__label">
          {getSignedInLabel(user?.name, user?.email)}
        </span>
      </div>

      <nav
        className="app-shell-topnav__nav"
        aria-label="Dashboard top navigation"
      >
        <NavLink
          to={dashboardHomePath}
          end
          style={({ isActive }) => ({
            ...topLinkStyle,
            color: isActive
              ? adminTheme.color.text
              : adminTheme.color.textSoft,
            background: isActive ? "#e2e8f0" : "transparent",
            fontWeight: isActive ? 700 : 600,
          })}
        >
          Dashboard
        </NavLink>

        <NavLink
          to="/staff/dashboard/providers"
          style={({ isActive }) => ({
            ...topLinkStyle,
            color: isActive
              ? adminTheme.color.text
              : adminTheme.color.textSoft,
            background: isActive ? "#e2e8f0" : "transparent",
            fontWeight: isActive ? 700 : 600,
          })}
        >
          Providers
        </NavLink>
      </nav>

      {user ? (
        <button type="button" onClick={logout} style={logoutButtonStyle}>
          Log out
        </button>
      ) : null}
    </header>
  );
}